import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Download, Search, Filter, RefreshCw, History,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";
import { PageHeader } from "../components/PageHeader";

// Acciones conocidas para el filtro de dropdown
const KNOWN_ACTIONS = [
  "org.create",
  "org.suspend",
  "org.reactivate",
  "org.delete.soft",
  "org.restore",
  "org.tier_override",
  "user.invite",
  "user.disable",
  "user.enable",
  "user.password_reset_initiated",
  "admin.grant",
  "admin.revoke",
  "tier.create",
  "tier.update",
  "tier.delete",
  "integrations.ai.update",
  "integrations.smtp.update",
  "system.banner",
  "demo_request.update",
];

const PAGE_LIMIT = 100;

const AuditLogPage = () => {
  const { token, user } = useAuth();
  const { t, locale } = useI18n();
  const isES = locale === "es";
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Filtros
  const [filterAction, setFilterAction] = useState("_all");
  const [filterActor, setFilterActor] = useState("");
  const [filterTarget, setFilterTarget] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Redirige si no es super-admin
  useEffect(() => {
    if (user && !user.is_super_admin) {
      toast.error(isES ? "Acceso restringido" : "Access denied");
      navigate("/dashboard");
    }
  }, [user, navigate, isES]);

  const buildParams = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    if (filterAction && filterAction !== "_all") params.set("action", filterAction);
    if (filterActor.trim()) params.set("actor", filterActor.trim());
    if (filterTarget.trim()) params.set("target", filterTarget.trim());
    if (filterFrom) params.set("from", filterFrom);
    if (filterTo) params.set("to", filterTo);
    params.set("limit", String(PAGE_LIMIT));
    params.set("offset", String(extra.offset ?? offset));
    return params;
  }, [filterAction, filterActor, filterTarget, filterFrom, filterTo, offset]);

  const fetchRows = useCallback(async (resetOffset = false) => {
    if (!token || !user?.is_super_admin) { setLoading(false); return; }
    setLoading(true);
    const off = resetOffset ? 0 : offset;
    if (resetOffset) setOffset(0);
    try {
      const params = buildParams({ offset: off });
      const res = await axios.get(`${API}/platform/audit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = Array.isArray(res.data) ? res.data : (res.data.items || []);
      setRows(data);
      setHasMore(data.length === PAGE_LIMIT);
    } catch (err) {
      toast.error(extractError(err, isES ? "Error al cargar el log" : "Failed to load audit log"));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, buildParams]);

  useEffect(() => {
    fetchRows(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterActor, filterTarget, filterFrom, filterTo]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = buildParams({ offset: 0 });
      params.set("format", "csv");
      params.set("limit", "500");
      const res = await axios.get(`${API}/platform/audit-log?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const now = new Date();
      const ts = now.toISOString().replace(/[T:]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `audit-log-${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(extractError(err, isES ? "Error al exportar" : "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const handlePrevPage = () => {
    const newOff = Math.max(0, offset - PAGE_LIMIT);
    setOffset(newOff);
    fetchRows(false);
  };

  const handleNextPage = () => {
    const newOff = offset + PAGE_LIMIT;
    setOffset(newOff);
    fetchRows(false);
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        title={t("platform_admin.audit.title")}
        subtitle={isES ? "Historial completo de acciones de administración" : "Complete history of admin actions"}
        icon={<History className="w-5 h-5" />}
      />

      <div className="flex items-center gap-2 mb-2">
        <Link to="/platform-admin">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {isES ? "Volver" : "Back"}
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {isES ? "Filtros" : "Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">{t("platform_admin.audit.filter_action")}</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={isES ? "Todas" : "All"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">{isES ? "Todas las acciones" : "All actions"}</SelectItem>
                  {KNOWN_ACTIONS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">{t("platform_admin.audit.filter_actor")}</Label>
              <Input
                className="h-8 text-xs"
                placeholder={isES ? "Email del actor" : "Actor email"}
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">{t("platform_admin.audit.filter_target")}</Label>
              <Input
                className="h-8 text-xs"
                placeholder={isES ? "ID u org" : "ID or org"}
                value={filterTarget}
                onChange={(e) => setFilterTarget(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">{t("platform_admin.audit.filter_from")}</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">{t("platform_admin.audit.filter_to")}</Label>
              <Input
                className="h-8 text-xs"
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla + acciones */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-sm">
              {isES ? `Mostrando ${rows.length} entrada(s)` : `Showing ${rows.length} entry(ies)`}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchRows(true)}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                {isES ? "Actualizar" : "Refresh"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                disabled={exporting}
                data-testid="export-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {t("platform_admin.audit.export_csv")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10">
              {isES ? "Sin actividad" : "No activity yet"}
            </div>
          ) : (
            <ScrollArea className="h-[60vh]">
              <div className="divide-y divide-border">
                {rows.map((row, i) => (
                  <div key={row.log_id || i} className="py-2 flex items-start gap-3 text-xs">
                    <Badge variant="outline" className="shrink-0 font-mono">{row.action}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{row.actor_email || "—"}</div>
                      <div className="text-muted-foreground truncate">
                        {row.target && Object.entries(row.target)
                          .filter(([, v]) => v)
                          .map(([k, v]) => `${k}=${v}`)
                          .join(" · ")}
                      </div>
                    </div>
                    <div className="text-muted-foreground shrink-0 tabular-nums">
                      {new Date(row.timestamp).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}

          {/* Paginación */}
          {!loading && (rows.length > 0 || offset > 0) && (
            <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
              <Button
                variant="ghost"
                size="sm"
                onClick={handlePrevPage}
                disabled={offset === 0}
              >
                {isES ? "Anterior" : "Previous"}
              </Button>
              <span>
                {isES
                  ? `Página ${Math.floor(offset / PAGE_LIMIT) + 1}`
                  : `Page ${Math.floor(offset / PAGE_LIMIT) + 1}`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNextPage}
                disabled={!hasMore}
              >
                {isES ? "Siguiente" : "Next"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
